import { Box, Tooltip, Typography } from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ContractDetailTextField } from "@features/projects-feature/components/ContractDetailTextField";
import { ContractDetailFields } from "@features/projects-feature/utils/fields/ContractDetailsFields";
import { ServiceContractDetailsContainerProps } from "@features/projects-feature/projectTypes";
import {
  StyledServiceContractDetailsContainer,
  StyledServiceContractDetailsSubheader,
} from "@features/projects-feature/utils/components/StyledServiceContractDetailsComponents";
import { ItemBox } from "shared-ui";
import { formatDollarAmount } from "shared-ui/src/features/roadmaps-feature/utils/roadmapHelpers";
import { usageBalanceHookRegistry } from "@features/services-feature/contracts/usageBalanceHookRegistry";

const getSortedColumnFields = (columnKey: number) => {
  return [...ContractDetailFields]
    .filter((field) => field.columnKey === columnKey)
    .sort((a, b) => a.sortPriority - b.sortPriority);
};
export const ServiceContractDetailsContainer = ({
  contract,
  picklist,
}: ServiceContractDetailsContainerProps) => {
  const contractDetailsColumnOne = getSortedColumnFields(1);
  const contractDetailsColumnTwo = getSortedColumnFields(2);

  const usageBalanceHook =
    usageBalanceHookRegistry[contract.billingTypeLabel] ||
    (() => ({
      usage: {},
      balance: {},
      balanceLabel: "Balance",
      isLoading: false,
      error: undefined,
    }));

  const { usage, balance, balanceLabel } = usageBalanceHook(contract.id);

  return (
    <StyledServiceContractDetailsContainer container spacing={2}>
      <Grid item xs={12}>
        <StyledServiceContractDetailsSubheader>
          Service Contract
        </StyledServiceContractDetailsSubheader>
      </Grid>
      {contractDetailsColumnOne.length > 0 && (
        <Grid item xs={6}>
          {contractDetailsColumnOne.map((field) => {
            return (
              <ContractDetailTextField
                key={field.fieldKey}
                field={field}
                contract={contract}
                picklist={picklist}
              />
            );
          })}
        </Grid>
      )}
      {contractDetailsColumnTwo.length > 0 && (
        <Grid item xs={6}>
          {contractDetailsColumnTwo.map((field) => {
            return (
              <ContractDetailTextField
                key={field.fieldKey}
                field={field}
                contract={contract}
                picklist={picklist}
              />
            );
          })}
        </Grid>
      )}

      {/* Usage metrics section */}
      {usage && Object.keys(usage).length > 0 && (
        <Grid item xs={12}>
          <Typography variant="body1" gutterBottom padding={2}>
            Usage
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(usage).map(([key, value]) => (
              <Grid item xs={4} key={key}>
                <ItemBox
                  label={key}
                  data={formatDollarAmount(value)}
                  labelVariant={"body1"}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}

      {/* Balance/Retainer section */}
      {balance && Object.keys(balance).length > 0 && (
        <Grid item xs={12}>
          <Typography variant="body1" gutterBottom padding={2}>
            {balanceLabel}
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(balance).map(([key, value]) => (
              <Grid item xs={4} key={key}>
                <ItemBox
                  label={
                    key === "Est. Support Dollars Remaining" ? (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="body1" color="text.secondary">
                          {key}
                        </Typography>
                        <Tooltip
                          title="Balance reflects usage through end of previous business day"
                          arrow
                        >
                          <InfoOutlinedIcon
                            fontSize="small"
                            sx={{ color: "text.secondary", cursor: "help" }}
                          />
                        </Tooltip>
                      </Box>
                    ) : (
                      key
                    )
                  }
                  data={formatDollarAmount(value)}
                  labelVariant={"body1"}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
    </StyledServiceContractDetailsContainer>
  );
};
